// src/types/errors.ts
import { TRPCClientError } from '@trpc/client';
import { TRPCError } from '@trpc/server';

import type { InfinexErrorResponse } from '@infinex-private/connect-types';
import { infinexErrorResponseSchema } from '@infinex-private/connect-types';

/**
 * Error instance types get lost when going over the DO boundary,
 * so we have to serialize them in a well-formed fashion instead.
 **/
export function throwSerializedInfinexError(
  errorResponse: InfinexErrorResponse
): never {
  throw new Error(JSON.stringify(errorResponse));
}

/**
 * This parses an error thrown by this package.
 **/
export function parseSerializedInfinexError(
  error: unknown
): InfinexErrorResponse | null {
  if (!(error instanceof Error)) return null;
  let parsedErrorMessage: unknown;
  try {
    parsedErrorMessage = JSON.parse(error.message);
  } catch {
    return null;
  }
  const parseResult = infinexErrorResponseSchema.safeParse(parsedErrorMessage);
  if (!parseResult.success) return null;
  return parseResult.data;
}

/** Extract a normalised InfinexErrorResponse from any tRPC/DO error */
export function getInfinexError(err: unknown): InfinexErrorResponse | null {
  /* ─────────── new: server-side tRPC branch ─────────── */
  if (err instanceof TRPCError) {
    const parsed = infinexErrorResponseSchema.safeParse(err.cause);
    if (parsed.success) return parsed.data;
  }

  /* 1. Client-side tRPC error (already handled) */
  if (err instanceof TRPCClientError) {
    const parsed = infinexErrorResponseSchema.safeParse(
      (err.data as any)?.cause
    );
    if (parsed.success) return parsed.data;
  }

  /* 2. DO-boundary JSON-stringified error (already handled) */
  const serialised = parseSerializedInfinexError(err);
  if (serialised) return serialised;

  /* 3. Native InfinexError instance (already handled) */
  if (isInfinexError((err as any)?.cause)) {
    return (err as any).cause.toErrorResponse();
  }

  return null;
}

export function isInfinexErrorResponse(
  err: unknown
): err is InfinexErrorResponse {
  return infinexErrorResponseSchema.safeParse(err).success;
}

export function isInfinexError(err: unknown): err is InfinexError {
  return err instanceof InfinexError;
}

export class InfinexError extends Error {
  public response: InfinexErrorResponse;

  constructor(errorResponse: InfinexErrorResponse) {
    super(errorResponse.message);
    this.name = 'InfinexError';
    this.response = errorResponse;

    // captureStackTrace to keep the stack clean in V8
    if ((Error as any).captureStackTrace) {
      (Error as any).captureStackTrace(this, InfinexError);
    }
    Object.setPrototypeOf(this, InfinexError.prototype);
  }

  public toErrorResponse(): InfinexErrorResponse {
    return this.response;
  }
}

/**
 * Helper to run an async fn with a fallback on failure.
 */
export async function withFallback<T>(
  fn: () => Promise<T>,
  fallback: T
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      'Async execution failed:',
      error,
      '→ returning fallback:',
      fallback
    );
    return fallback;
  }
}
